import React, {useEffect, useState} from 'react';
import {Alert, Button, Dialog} from '@betareader/design-system';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import {Question} from '../../content/survey/question/question';
import {updateCurrentBook} from '../../../modules/book';
import {updateReaderSignupQuestions} from '../../../modules/survey';
import {authUtil} from '../../../utils';

const emptyQuestions = [];

const JoinBetaFormDesigner = ({
  questions: questionsFromProps = emptyQuestions,
  readOnly,
  trigger,
  asModal
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const currentBook = useSelector(state => state.book.currentBook);
  const updatingSignUpSurvey = useSelector(state => state.survey.updatingSignUpSurvey);
  const [showModal, setShowModal] = useState(false);
  const [showQuestionsInline, setShowQuestionsInline] = useState(false);
  const [questions, setQuestions] = useState(questionsFromProps);

  useEffect(() => {
    setQuestions(questionsFromProps);
  }, [questionsFromProps]);

  const addQuestion = () => {
    setQuestions(current => current.length < 3 ? [...current, {
      kind: 'LONG_TEXT',
      options: {},
      question: `${t('Question')} ${current.length + 1}`
    }] : current);
  };

  const deleteQuestion = index => {
    setQuestions(current => current.filter((_, questionIndex) => questionIndex !== index));
  };

  const updateQuestion = ({index, ...change}) => {
    setQuestions(current => current.map((question, questionIndex) =>
      questionIndex === index ? {...question, ...change} : question));
  };

  const saveQuestions = async () => {
    if (!currentBook || readOnly || updatingSignUpSurvey) return;
    const idToken = await authUtil.getFreshIdToken();
    const surveyData = {
      ...currentBook.readerSignupSurvey,
      book: currentBook._id,
      questions,
      placement: 'READER_SIGNUP'
    };
    dispatch(updateReaderSignupQuestions(idToken, {
      surveyData,
      bookId: currentBook._id
    }, returnedSurvey => {
      dispatch(updateCurrentBook({
        ...currentBook,
        readerSignupSurvey: returnedSurvey
      }));
    }));
    setShowModal(false);
  };

  const questionEditor = <fieldset disabled={readOnly}
    style={{border: 0, padding: 0, margin: 0,
      display: 'grid', gap: 'var(--br-space-3)'}}>
    {questions.length > 0 && <Alert>
      {t('YouCanAskUpToXQuestions', {maxQuestions: 3})}
    </Alert>}
    {questions.map((question, index) => <Question key={index}
      t={t} index={index} question={question}
      deleteQuestion={() => deleteQuestion(index)} updateQuestion={updateQuestion} />)}
    {questions.length < 3 && <Button variant='secondary' disabled={readOnly}
      onClick={addQuestion}>
      {questions.length === 0
        ? t('YouCanAskUpToXQuestions', {maxQuestions: 3}) : t('NewQuestion')}
    </Button>}
  </fieldset>;

  if (asModal) {
    return <>
      <Button variant={trigger === 'Dropdown.Item' ? 'ghost' : 'secondary'}
        onClick={() => setShowModal(true)}>
        {t('ReaderSignUpForm')}
      </Button>
      <Dialog open={showModal} title={t('JoinBeta')}
        onClose={() => setShowModal(false)}
        actions={<>
          <Button variant='secondary' onClick={() => setShowModal(false)}>
            {t('Cancel')}
          </Button>
          <Button disabled={readOnly || updatingSignUpSurvey}
            aria-busy={Boolean(updatingSignUpSurvey)} onClick={saveQuestions}>
            {t('Save')}
          </Button>
        </>}>
        {questionEditor}
      </Dialog>
    </>;
  }

  return <div style={{display: 'grid', gap: 'var(--br-space-3)'}}>
    {showQuestionsInline ? <>
      {questionEditor}
      {(questions.length > 0 || questionsFromProps.length !== questions.length) &&
        <Button disabled={readOnly || updatingSignUpSurvey}
          aria-busy={Boolean(updatingSignUpSurvey)} onClick={saveQuestions}>
          {t('Save')}
        </Button>}
    </> : <Button variant='secondary' disabled={readOnly}
      onClick={() => {
        setShowQuestionsInline(true);
        if (questions.length === 0) addQuestion();
      }}>
      {questionsFromProps.length === 0
        ? t('AddSignUpSurveyQuestions') : t('EditSignUpSurveyQuestions')}
    </Button>}
  </div>;
};

export default JoinBetaFormDesigner;
